import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useEpisodes } from '../hooks/useEpisodes';
import { EpisodeSkeleton } from '../components/EpisodeSkeleton';
import { EpisodeCard } from '../components/EpisodeCard';
import { SearchBar } from '../components/SearchBar';
import { TagFilter } from '../components/TagFilter';
import { Newsletter } from '../components/Newsletter';

// Threshold for showing search/filter UI
// When episode count is below this, the UI is hidden to reduce clutter
const SEARCH_FILTER_THRESHOLD = 10;

// Toggle to enable/disable search/filter UI (set to false to hide it completely)
const ENABLE_SEARCH_FILTER = false;

const EpisodesPage: React.FC = () => {
  // Load all episodes dynamically
  const { episodes: allEpisodes, loading, error } = useEpisodes();

  // Get all unique tags (memoized to avoid recalculating on every render)
  const allTags = useMemo(() =>
    Array.from(new Set(allEpisodes.flatMap(ep => ep.tags))).sort(),
    [allEpisodes]
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState<string>('all');

  // Determine if search/filter UI should be shown
  const showSearchAndFilter = ENABLE_SEARCH_FILTER && allEpisodes.length >= SEARCH_FILTER_THRESHOLD;

  // Filter episodes (only apply filters if search/filter UI is visible)
  const filteredEpisodes = showSearchAndFilter
    ? allEpisodes.filter(episode => {
        const matchesSearch = searchQuery === '' ||
          episode.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          episode.excerpt.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesTag = selectedTag === 'all' || episode.tags.includes(selectedTag);

        return matchesSearch && matchesTag;
      })
    : allEpisodes;

  // Calculate stats
  const totalEpisodes = allEpisodes.length;
  const totalTags = allTags.length;
  const avgReadTime = Math.round(allEpisodes.reduce((sum, ep) => sum + ep.readingTime, 0) / totalEpisodes);

  return (
    <div className="min-h-screen bg-black">
      {/* Back button */}
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">

          {/* Page Header */}
          <div className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-100 mb-4">
              Chronicles from the Basement
            </h1>
            <p className="text-xl text-gray-400 mb-8">
              Every ridiculous user request. Every theatrical solution. Every PFY training moment.
              <br className="hidden sm:block" />
              Filed, tagged, and ready for your amusement.
            </p>

            {/* Stats Bar */}
            <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-gray-500 mb-8">
              <div className="flex items-center gap-2">
                <span className="text-2xl">📚</span>
                <span><strong className="text-gray-300">{totalEpisodes}</strong> Episodes</span>
              </div>
              <span className="text-gray-700">•</span>
              <div className="flex items-center gap-2">
                <span className="text-2xl">🏷️</span>
                <span><strong className="text-gray-300">{totalTags}</strong> Tags</span>
              </div>
              <span className="text-gray-700">•</span>
              <div className="flex items-center gap-2">
                <span className="text-2xl">⏱️</span>
                <span><strong className="text-gray-300">~{avgReadTime} min</strong> average read</span>
              </div>
            </div>
          </div>

          {/* Search & Filter Bar - Only shown when episode count reaches threshold */}
          {showSearchAndFilter && (
            <div className="mb-8 space-y-4">
              {/* Search Box */}
              <SearchBar
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                placeholder="Search episodes..."
              />

              {/* Tag Filter */}
              <TagFilter
                allTags={allTags}
                selectedTag={selectedTag}
                onTagSelect={setSelectedTag}
                totalEpisodes={totalEpisodes}
                filteredCount={filteredEpisodes.length}
                onClearFilters={() => {
                  setSearchQuery('');
                  setSelectedTag('all');
                }}
                showActiveFilters={searchQuery !== '' || selectedTag !== 'all'}
              />
            </div>
          )}

          {/* Episodes Grid */}
          {loading ? (
            <div className="grid sm:grid-cols-2 gap-6 mb-12">
              <EpisodeSkeleton />
              <EpisodeSkeleton />
              <EpisodeSkeleton />
              <EpisodeSkeleton />
              <EpisodeSkeleton />
              <EpisodeSkeleton />
              <EpisodeSkeleton />
              <EpisodeSkeleton />
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">⚠️</div>
              <h3 className="text-2xl font-bold text-gray-100 mb-2">Failed to load episodes</h3>
              <p className="text-red-500 mb-6">{error.message}</p>
              <button
                onClick={() => window.location.reload()}
                className="btn btn-secondary"
              >
                Retry
              </button>
            </div>
          ) : filteredEpisodes.length > 0 ? (
            <div className="grid sm:grid-cols-2 gap-6 mb-12">
              {filteredEpisodes.map((episode) => (
                <EpisodeCard
                  key={episode.slug}
                  episode={episode}
                  onTagClick={setSelectedTag}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold text-gray-100 mb-2">No episodes found</h3>
              <p className="text-gray-400 mb-6">
                Try adjusting your search or filter criteria
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedTag('all');
                }}
                className="btn btn-secondary"
              >
                Clear filters
              </button>
            </div>
          )}

          {/* Subscribe CTA */}
          <Newsletter variant="inline" disclaimer="No spam. Unsubscribe anytime. We barely store it." />

        </div>
      </div>
    </div>
  );
};

export default EpisodesPage;
